// src/components/AboutPanelOverlay.tsx
import React, { useEffect, useState } from "react";
import "./AboutPanelOverlay.css";

// Resolve build string safely across bundlers (Vite, CRA, or window fallback)
const BUILD: string = (() => {
  try {
    // Vite style (allowed by Webpack if we only do property/destructure access)
    const { env } = (import.meta as any);
    if (env?.VITE_CUEBOARD_BUILD) return String(env.VITE_CUEBOARD_BUILD);
  } catch {
    // ignore if import.meta isn't available in this bundler/runtime
  }

  // CRA / Webpack style: process.env.REACT_APP_BUILD
  if (typeof process !== "undefined" && (process as any)?.env?.REACT_APP_BUILD) {
    return String((process as any).env.REACT_APP_BUILD);
  }

  // Window fallback: set window.CUEBOARD_BUILD in index.html if desired
  if (typeof window !== "undefined" && (window as any)?.CUEBOARD_BUILD) {
    return String((window as any).CUEBOARD_BUILD);
  }

  return "The Operating System for Professional Conversations";
})();

export default function AboutPanelOverlay() {
  const [open, setOpen] = useState(false);

  // Toggle with Alt+/ (Win) or Cmd+/ (Mac). Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isCmd = e.metaKey && e.key === "/";
      const isAlt = e.altKey && e.key === "/";
      if (isCmd || isAlt) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      {/* Tiny floating info button (overlay, not part of page toolbars) */}
      <button className="about-fab" title="About (Alt+/)" onClick={() => setOpen(true)}>
        ⓘ
      </button>

      {open && (
        <div className="about-backdrop" onClick={() => setOpen(false)}>
          <div
            className="about-panel"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="about-header">
              <div className="about-title">
                <strong>CueBoard</strong>
                <span className="about-build"> • {BUILD}</span>
              </div>
              <button className="about-close" onClick={() => setOpen(false)} aria-label="Close">
                ✕
              </button>
            </header>

            <section className="about-section">
              <h3>About CueBoard</h3>
              <p>
               CueBoard is the voice-activated cue card for high-stakes conversations. Whether it is a job interview, a sales call, or a tough customer meeting, CueBoard acts as your in-the-moment layer, surfacing your prep in response to what's being said. 
              </p>
            </section>

            <section className="about-section">
              <h3>What CueBoard does</h3>
              <ul>
                <li><strong>Reliable trigger phrases</strong>: CueBoard listens for key phrases.</li>          
                <li><strong>Real-time recall for high-stakes conversations</strong>: Your notes appear exactly when you need them.</li>           
                <li><strong>Lightweight & distraction-free</strong>: A calm, floating, non-intrusive window.</li>
              </ul>
            </section>

            <section className="about-section">
              <h3>Why we built CueBoard</h3>
              <p>
               Everyone has blanked in a moment that mattered. CueBoard exists to make sure that never costs you an opportunity again. It's not about replacing you with AI. It's about empowering you to bring your best self forward.
              </p>
            </section>

            <section className="about-section">
              <h3>What’s next</h3>
              <p>
               We're starting with job seekers, but our vision is much bigger. We plan to expand into any high-stakes conversation where showing up prepared changes the outcome.
              </p>
            </section>

            <section className="about-section about-links">
              <a href="https://getcueboard.com" target="_blank" rel="noopener noreferrer">
                Learn more at getcueboard.com
              </a>
            </section>
          </div>
        </div>
      )}
    </>
  );
}
